import { BASE_URLS } from "./axios";

export { BASE_URLS };

/* ---------- Safe path join helper ---------- */
const join = (base, path) =>
  `${String(base ?? "").replace(/\/$/, "")}${String(path ?? "")}`;

/* ===== AUTH ===== */
export const AUTH_PATHS = {
  LOGIN: "/auth/login",
  VERIFY_TOKEN: "/auth/verify",
  REQUEST_RESET: "/auth/password/request-reset",
  VERIFY_RESET_CODE: "/auth/password/verify-code",
  RESET_PASSWORD: "/auth/password/reset",
  VERIFY_2FA: "/auth/verify-2fa",
  LOGOUT: "/auth/logout",
  VERIFY_CODE: "/auth/verify-code",
  RESEND_2FA: "/auth/resend-2fa-code",
};

/* ===== OAuth URLs ===== */
export const OAUTH_URLS = {
  GOOGLE: join(BASE_URLS.GATEWAY, "/auth/google/login"),
  FACEBOOK: join(BASE_URLS.GATEWAY, "/auth/facebook/login"),
  GITHUB: join(BASE_URLS.GATEWAY, "/auth/github"),
};

/* ===== USERS ===== */
export const USERS_PATHS = {
  REGISTER: "/register",
  OAUTH_UPSERT: "/oauth/upsert",
  BASE: "/",
  BY_ID: (id) => `/${id}`,
  UPDATE: (id) => `/${id}`,
  DELETE: (id) => `/${id}`,
  ME: "/me",
  AVATAR_UPLOAD: (id) => `/${id}/avatar`,
  ADDITIONAL_INFO: (id) => `/${encodeURIComponent(String(id))}/additional-info`,
  SEARCH: "/search",
  ALL: "/users",
};

/* ===== MEDICAL ===== */
export const MEDICAL_PATHS = {
  BASE: "/",
  CREATE: (userId) => `/${userId}`,
  BY_USER: (userId) => `/${userId}`,
  UPDATE: (userId) => `/${userId}`,
  DELETE: (userId) => `/${userId}`,

  BMI_HISTORY: (userId) => `/${userId}/bmi-history`,
  UPDATE_BMI_HISTORY: (userId, id) => `/${userId}/bmi-history/${id}`,

  PERSONAL_HISTORY: (userId) => `/${userId}/personal-history`,
  PERSONAL_HISTORY_TIMELINE: (userId) => `/${userId}/personal-history/history`,
  ADD_PERSONAL_HISTORY: (userId) => `/${userId}/personal-history`,
  UPDATE_PERSONAL_HISTORY: (userId, id) => `/${userId}/personal-history/${id}`,
  DELETE_PERSONAL_HISTORY: (userId, id) => `/${userId}/personal-history/${id}`,

  SURGICAL_HISTORY: (userId) => `/${userId}/surgical-history`,
  UPDATE_SURGICAL_HISTORY: (userId, id) => `/${userId}/surgical-history/${id}`,
  DELETE_SURGICAL_HISTORY: (userId, id) => `/${userId}/surgical-history/${id}`,

  FAMILY_HISTORY: (userId) => `/${userId}/family-history`,
  UPDATE_FAMILY_HISTORY: (userId, id) => `/${userId}/family-history/${id}`,
  DELETE_FAMILY_HISTORY: (userId, id) => `/${userId}/family-history/${id}`,

  ALLERGIES: (userId) => `/${userId}/allergies`,
  UPDATE_ALLERGY: (userId, id) => `/${userId}/allergies/${id}`,
  DELETE_ALLERGY: (userId, id) => `/${userId}/allergies/${id}`,

  VACCINATIONS: (userId) => `/${userId}/vaccinations`,
  UPDATE_VACCINATION: (userId, id) => `/${userId}/vaccinations/${id}`,
  DELETE_VACCINATION: (userId, id) => `/${userId}/vaccinations/${id}`,

  VITALS: (userId) => `/${userId}/vitals`,
  UPDATE_VITAL: (userId, id) => `/${userId}/vitals/${id}`,
  DELETE_VITAL: (userId, id) => `/${userId}/vitals/${id}`,

  BY_ID: (id) => `/record/${id}`,
  UPDATE_BY_ID: (id) => `/record/${id}`,
  DELETE_BY_ID: (id) => `/record/${id}`,

  INSURANCE_PROVIDERS: "/insurance-providers",
};

/* ===== CYCLES / CALENDAR ===== */
export const CYCLES_PATHS = {
  BASE: "/cycles",
  BY_USER: (userId) => `/cycles/${userId}`,
  PREDICTIONS: (userId) => `/cycles/${userId}/predictions`,
  FERTILITY_WINDOW: (userId) => `/cycles/${userId}/fertility-window`,
  SUMMARY: (userId) => `/cycles/${userId}/summary`,
};

/* ===== RELATIVES ===== */
export const RELATIVES_PATHS = {
  BASE: "/relatives",
  BY_USER: (userId) => `/relatives/${userId}`,
  BY_ID: (id) => `/relatives/${id}`,
  ACCEPT: (id) => `/relatives/accept/${id}`,
};

/* ===== POSTS ===== */
export const POSTS_PATHS = {
  BASE: "/posts",

  BY_ID: (id) => `/posts/${encodeURIComponent(String(id))}`,

  UPLOAD: "/posts/upload",

  CREATE: (userId) => `/posts/create/${encodeURIComponent(String(userId))}`,

  BY_USER: (userId) => `/posts/user/${encodeURIComponent(String(userId))}`,

  UPDATE_BY_POST_ID: (postId, userId) =>
    `/posts/update/by-post-id/${encodeURIComponent(String(postId))}/${encodeURIComponent(String(userId))}`,

  UPDATE_BY_ID: (id, userId) =>
    `/posts/update/${encodeURIComponent(String(id))}/${encodeURIComponent(String(userId))}`,

  DELETE_BY_POST_ID: (postId, userId) =>
    `/posts/delete/by-post-id/${encodeURIComponent(String(postId))}/${encodeURIComponent(String(userId))}`,

  DELETE_BY_ID: (id) => `/posts/delete/${encodeURIComponent(String(id))}`,
};

/* ===== MEDICATION ===== */
export const MEDICATION_PATHS = {
  ALL: (userId) => `/medication/${userId}/all`,
  TODAY: (userId) => `/medication/${userId}/today`,
  FOR_DATE: (userId) => `/medication/${userId}/for-date`,
  ARCHIVED: (userId) => `/medication/${userId}/archived`,

  ONE: (userId, id) => `/medication/${userId}/get/${id}`,
  CREATE: (userId) => `/medication/${userId}/create`,
  UPDATE: (userId, id) => `/medication/${userId}/${id}/update`,

  TAKE: (userId, id, idx) => `/medication/${userId}/${id}/take/${idx}`,
  TOGGLE: (userId, id) => `/medication/${userId}/${id}/toggle-alarm`,
  ARCHIVE: (userId, id) => `/medication/${userId}/${id}/archive`,

  DELETE: (userId, id) => `/medication/${userId}/delete/${id}`,
  CHECK_ARCHIVE: (userId) => `/medication/${userId}/check-archive`,

  HISTORY: (userId, id) => `/medication/${userId}/${id}/history`,
  STATS: (userId, id) => `/medication/${userId}/${id}/stats`,

  RESET_DOSES: (userId, id) => `/medication/${userId}/${id}/reset-doses`,
  RESET_ALL_DOSES: (userId) => `/medication/${userId}/reset-all-doses`,

  STOCK_REMINDER_SENT: (userId, id) =>
    `/medication/${userId}/${id}/stock-reminder-sent`,
};

/* ===== MEDICATIONS DATABASE ===== */
export const MEDICATIONS_DATABASE_PATHS = {
  BASE: "/medications",
  BY_ID: (medicationId) => `/medications/${encodeURIComponent(String(medicationId))}`,
  SEARCH: "/medications/search",
  STATS: "/medications/stats",
};

/* ===== PLACES (DB-only) ===== */
export const PLACES_PATHS = {
  BASE: "/places",
  CATEGORY_BASE: "/places/category",
  LIST_BY_CATEGORY: "/places/category/:category",
  BY_ID: "/places/category/:category/by-id/:placeId",
  DEBUG: "/places/category/:category/_debug",
  PHOTO: "/places/photo",
};

/* ===== EMERGENCY CONTACTS ===== */
export const EMERGENCY_PATHS = {
  BASE: "/emergency-contact",
  LIST: (userId) => `/emergency-contact/all/${userId}`,
  CREATE: (userId) => `/emergency-contact/add/${userId}`,
  BY_ID: (userId, id) => `/emergency-contact/one/${userId}/${id}`,
  UPDATE: (userId, id) => `/emergency-contact/update/${userId}/${id}`,
  DELETE: (userId, id) => `/emergency-contact/delete/${userId}/${id}`,
};

/* ===== ANALYSES ===== */
export const ANALYSE_PATHS = {
  BASE: "/analyse",
  LIST: (userId) => `/analyse/${userId}`,
  UPLOAD: (userId) => `/analyse/${userId}/upload`,
  UPLOADVD: (userId) => `/analyse/${userId}/upload-video`,
  BY_ID: (userId, id) => `/analyse/${userId}/${id}`,
};

/* ===== VITAL SIGNS ===== */
export const VITALS_PATHS = {
  BASE: "/vital-signs",
  ALL: (userId) => `/vital-signs/all/${userId}`,
  ADD: (userId) => `/vital-signs/add/${userId}`,
  ONE: (userId, id) => `/vital-signs/one/${userId}/${id}`,
  DELETE: (userId, id) => `/vital-signs/delete/${userId}/${id}`,
};

/* ===== MODERATION ===== */
export const MODERATION_PATHS = {
  BASE: "/",
  HEALTH: "/health",
  MODERATE: "/moderate",
  MODERATE_BATCH: "/moderate/batch",
  POSTS: "/posts",
  POST_BY_ID: (id) => `/posts/${id}`,
  POSTS_BY_USER: (userId) => `/posts/user/${userId}`,
  UPDATE_POST: (idPost) => `/posts/by-post-id/${idPost}`,
  DELETE_POST: (idPost) => `/posts/by-post-id/${idPost}`,
  STATS: "/stats",
};

/* ===== MESSAGES ===== */
export const MESSAGES_PATHS = {
  BASE: "/messages",
  SEND: "/messages",
  LIST(userA, userB) {
    return `/messages?${new URLSearchParams({ userA, userB }).toString()}`;
  },
  UPDATE(id) {
    return `/messages/${encodeURIComponent(String(id))}`;
  },
  DELETE(id) {
    return `/messages/${encodeURIComponent(String(id))}`;
  },
  DELETE_ALL(a, b) {
    return `/messages?${new URLSearchParams({ userA: a, userB: b }).toString()}`;
  },
  UPLOAD: "/messages/upload",
  WHOAMI: "/messages/whoami",
  CONTACTS_ME: "/messages/contacts/me",
  CONV_DOCTOR_TO_DOCTOR: "/messages/conversations/doctor-to-doctor",
  CONV_DOCTORS: "/messages/conversations/doctors",
  CONV_MESSAGES(id) {
    return `/messages/conversations/${encodeURIComponent(String(id))}/messages`;
  },
  CONV_READ(id) {
    return `/messages/conversations/${encodeURIComponent(String(id))}/read`;
  },
  RESTRICTION_STATUS(peerId) {
    return `/messages/restrictions/status/${encodeURIComponent(String(peerId))}`;
  },
  RESTRICTION(peerId) {
    return `/messages/restrictions/${encodeURIComponent(String(peerId))}`;
  },
};

/* ===== PRESCRIPTIONS ===== */
export const PRESCRIPTION_PATHS = {
  BASE: "/prescriptions",

  TEMPLATE: {
    CREATE: (doctorId) =>
      `/prescriptions/template/create/${encodeURIComponent(String(doctorId))}`,
    GET: (doctorId) =>
      `/prescriptions/template/${encodeURIComponent(String(doctorId))}`,
  },

  CREATE: (doctorId) =>
    `/prescriptions/create/${encodeURIComponent(String(doctorId))}`,

  CLASSIFY_SAVE: (doctorId) =>
    `/prescriptions/classify-save/${encodeURIComponent(String(doctorId))}`,

  BY_DOCTOR: (doctorId) =>
    `/prescriptions/all/${encodeURIComponent(String(doctorId))}`,

  BY_PATIENT: (doctorId, patientId) =>
    `/prescriptions/patient/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(patientId)
    )}`,

  BY_CONSULTATION: (doctorId, consultationId) =>
    `/prescriptions/consultation/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(consultationId)
    )}`,

  ONE: (doctorId, id) =>
    `/prescriptions/one/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(id)
    )}`,

  UPDATE: (doctorId, id) =>
    `/prescriptions/update/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(id)
    )}`,

  DELETE: (doctorId, id) =>
    `/prescriptions/delete/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(id)
    )}`,

  GENERATE_PDF: (doctorId, id) =>
    `/prescriptions/generate-pdf/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(id)
    )}`,

  DOWNLOAD_PDF: (doctorId, id) =>
    `/prescriptions/download-pdf/${encodeURIComponent(String(doctorId))}/${encodeURIComponent(
      String(id)
    )}`,

  PATIENT_SELF: () => `/prescriptions/patient-self`,
};

/* ===== PRESCRIPTION GUARD (AI SAFETY) ===== */
export const MEDICAL_INTERRACTION_PATHS = {
  ANALYZE_TEXT: "/interraction/analyze-text",
};

/* ===== DOCTOR AUTH ===== */
export const DOCTOR_AUTH_PATHS = {
  LOGIN: "/auth/login-dr",
  VERIFY: "/auth/doctor/verify",
  REQUEST_RESET: "/auth/doctor/request-reset",
  RESET_PASSWORD: "/auth/doctor/reset-password",
  RESEND_CODE: "/auth/doctor/resend-code",
  LOGOUT: "/auth/doctor/logout",

  PROFILE_ME: "/doctors/me",
  PROFILE_BY_ID: (id) => `/doctors/${id}`,
  DOCTORS: "/doctors",
  SEARCH_BY_NAME: (fullName) =>
    `/doctors/search/${encodeURIComponent(String(fullName))}`,
};

/* ===== REGISTER DOCTORS ===== */
export const DOCTOR_REGISTER_PATHS = {
  BASE: "/register-doctors",
  LIST: "/register-doctors",
  CREATE: "/register-doctors",
  BY_ID: (id) => `/register-doctors/${id}`,
  UPDATE: (id) => `/register-doctors/${id}`,
  DELETE: (id) => `/register-doctors/${id}`,
  SEARCH_BY_NAME: (fullName) => `/register-doctors/search/${encodeURIComponent(fullName)}`,
};

/* ===== EXTRACTION (Medical Documents) ===== */
export const EXTRACTION_PATHS = {
  HEALTH: "/health",
  STATISTICS: "/statistics",
  MY_DOCUMENTS: "/my-documents",

  USER_STORAGE: "/user-storage",
  RECALCULATE_STORAGE: "/user-storage/recalculate",

  PROCESS: "/process-document",

  DOCUMENTS: (collection) => `/${collection}`,
  DOCUMENT_BY_ID: (collection, id) => `/${collection}/${id}`,
  DELETE: (collection, id) => `/${collection}/${id}`,
  SEARCH: (collection) => `/search/${collection}`,

  UPDATE_PARAMETERS: (collection, id) => `/document/${collection}/${id}/update-parameters`,

  DOCUMENT_HISTORY: (collection, id) => `/document/${collection}/${id}/history`,

  FILE: (collection, filename) => `/consultation/${collection}/${filename}`,

  TRASH: {
    MOVE:             (collection, documentId) => `/trash/move/${collection}/${documentId}`,
    LIST:             `/trash`,
    RESTORE:          (trashId) => `/trash/${trashId}/restore`,
    PERMANENT_DELETE: (trashId) => `/trash/${trashId}`,
    BULK_RESTORE:     `/trash/bulk-restore`,
    BULK_DELETE:      `/trash/bulk-delete`,
    EMPTY:            `/trash`,
  },
  // Sharing
  SHARE_DOCUMENT: (documentId) => `/sharing/${encodeURIComponent(String(documentId))}/share`,
  SHARING_ACCESS: (documentId) => `/sharing/${encodeURIComponent(String(documentId))}/access`,
  SHARING_DOCUMENT: (documentId) => `/sharing/${encodeURIComponent(String(documentId))}/document`,
  SHARING_CAN_ACCESS: (documentId) => `/sharing/${encodeURIComponent(String(documentId))}/can-access`,
  SHARING_PATIENT_REVOKE_ALL: (doctorId) =>
    `/sharing/patient/revoke-all/${encodeURIComponent(String(doctorId))}`,
  SHARING_EXTEND_ACCESS: (documentId, doctorId) =>
    `/sharing/${encodeURIComponent(String(documentId))}/access/${encodeURIComponent(String(doctorId))}/extend`,
  SHARING_DOCTOR_MY_DOCUMENTS: "/sharing/doctor/my-shared-documents",
  SHARING_PATIENT_DOCTORS_WITH_ACCESS: "/sharing/patient/doctors-with-access",
  SHARING_STATISTICS: "/sharing/statistics",
  TRASH: {
    MOVE:             (collection, documentId) => `/trash/move/${collection}/${documentId}`,
    LIST:             `/trash`,
    RESTORE:          (trashId) => `/trash/${trashId}/restore`,
    PERMANENT_DELETE: (trashId) => `/trash/${trashId}`,
    BULK_RESTORE:     `/trash/bulk-restore`,
    BULK_DELETE:      `/trash/bulk-delete`,
    EMPTY:            `/trash`,
  },
};

/* ===== AVAILABILITY ===== */
export const AVAILABILITY_PATHS = {
  GET_ALL_SLOTS: (medecinId) => `/disponibilite/${medecinId}/all-slots`,
  GET_SLOTS_FOR_DAY: (medecinId, date) =>
    `/disponibilite/${medecinId}/slots${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  GET_AVAILABLE_SLOTS: (medecinId, date) =>
    `/disponibilite/${medecinId}/available-slots${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  GET_BLOCKED_SLOTS: (medecinId, date) =>
    `/disponibilite/${medecinId}/blocked-slots${date ? `?date=${encodeURIComponent(date)}` : ''}`,
  CREATE_RANGE: (medecinId) => `/disponibilite/${medecinId}`,
  UPDATE: (id) => `/disponibilite/${id}`,
  DELETE: (id) => `/disponibilite/${id}`,
  MARK_RANGE_UNAVAILABLE: `/disponibilite/range/mark-unavailable`,
  MARK_RANGE_AVAILABLE: `/disponibilite/range/mark-available`,
  MARK_SLOTS_UNAVAILABLE: `/disponibilite/slots/mark-unavailable`,
  MARK_SLOTS_AVAILABLE: `/disponibilite/slots/mark-available`,
};

/* ===== EVENTS ===== */
export const EVENTS_PATHS = {
  BASE: "/",
  CREATE: (doctorId) => `/${doctorId}`,
  BY_DOCTOR: (doctorId) => `/doctor/${encodeURIComponent(String(doctorId))}`,
  BY_DAY: (date) => `/date/${date}`,
  BY_RANGE: (startDate, endDate) => `/date-range?startDate=${startDate}&endDate=${endDate}`,
  UPDATE: (id) => `/${id}`,
  DELETE: (id) => `/${id}`,
  HARD_DELETE: (id) => `/${id}/hard`,
};

/* ===== RENDEZVOUS ===== */
export const RENDEZVOUS_PATHS = {
  BASE: () => `/`,

  CREATE: (patientId, medecinId) =>
    `/medecin/${encodeURIComponent(String(medecinId))}/patient/${encodeURIComponent(String(patientId))}`,

  // Patient-initiated booking — ownership-checked against the patient's own
  // userId, unlike CREATE above which is doctor/assistant-only.
  CREATE_BY_PATIENT: (userId, medecinId) =>
    `/users/${encodeURIComponent(String(userId))}/medecin/${encodeURIComponent(String(medecinId))}`,

  UPDATE: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}`,

  RESCHEDULE: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}/reschedule`,

  CANCEL: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}/cancel`,

  CANCEL_BY_PATIENT: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}/cancel/patient`,

  CANCEL_BY_DOCTOR: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}/cancel/doctor`,

  CONFIRM: (userId, id) =>
    `/users/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(id))}/confirm`,

  BY_MEDECIN: (medecinId) =>
    `/users/${encodeURIComponent(String(medecinId))}/medecin`,

  BY_PATIENT_EXTERNAL: (patientId) =>
    `/users/${encodeURIComponent(String(patientId))}/patient`,

  CONFIRMED: (userId, period) =>
    `/users/${encodeURIComponent(String(userId))}/confirmed/${encodeURIComponent(String(period))}`,

  SCHEDULED: (userId, period) =>
    `/users/${encodeURIComponent(String(userId))}/scheduled/${encodeURIComponent(String(period))}`,

  RESCHEDULED: (userId, period) =>
    `/users/${encodeURIComponent(String(userId))}/rescheduled/${encodeURIComponent(String(period))}`,
};

/* ===== LOCAL PATIENTS ===== */
export const LOCAL_PATIENT_PATHS = {
  BASE: () => `/`,
  CREATE: () => `/`,
  BY_ID: (id) => `/${encodeURIComponent(String(id))}`,
  BY_IDENTIFIER: (identifier) =>
    `/identifier/${encodeURIComponent(String(identifier))}`,
  ALL: (activeOnly) => activeOnly ? `?activeOnly=${activeOnly}` : `/`,
  UPDATE: (id) => `/${encodeURIComponent(String(id))}`,
  DEACTIVATE: (id) => `/${encodeURIComponent(String(id))}/deactivate`,
  REACTIVATE: (id) => `/${encodeURIComponent(String(id))}/reactivate`,
  DELETE: (id) => `/${encodeURIComponent(String(id))}`,
  ADDITIONAL_INFO: (id) =>
    `/${encodeURIComponent(String(id))}/additional-info`,
};

/* ===== ROOMS (WAITING ROOM) ===== */
export const ROOM_PATHS = {
  CREATE: (appointmentId) =>
    `/appointment/${encodeURIComponent(String(appointmentId))}/room`,

  CREATE_ON_ARRIVAL: (appointmentId) =>
    `/appointment/${encodeURIComponent(String(appointmentId))}/room/arrival`,

  BY_APPOINTMENT: (appointmentId) =>
    `/appointment/${encodeURIComponent(String(appointmentId))}/room`,

  ALL: () => `/room`,

  BY_STATUS: (status) =>
    `/room/status/${encodeURIComponent(String(status))}`,

  BY_DOCTOR: (doctorId) =>
    `/room/doctor/${encodeURIComponent(String(doctorId))}`,

  BY_PATIENT: (patientId) =>
    `/room/patient/${encodeURIComponent(String(patientId))}`,

  BY_ID: (roomId) =>
    `/room/${encodeURIComponent(String(roomId))}`,

  UPDATE_STATUS: (roomId, status) =>
    `/room/${encodeURIComponent(String(roomId))}/status/${encodeURIComponent(String(status))}`,

  DELETE: (roomId) =>
    `/room/${encodeURIComponent(String(roomId))}`,
};

/* ===== DOCTOR PROFILE SERVICE ===== */
export const DOCTOR_PROFILE_PATHS = {
  PING: "/ping",

  DOCTOR_INFO: {
    CREATE: "/doctor-info",
    UPSERT: "/doctor-info/upsert",
    GET_BY_DOCTOR_ID: (doctorId) => `/doctor-info/by-doctor/${encodeURIComponent(String(doctorId))}`,
    UPDATE_BY_DOCTOR_ID: (doctorId) => `/doctor-info/by-doctor/${encodeURIComponent(String(doctorId))}`,
    DELETE_BY_DOCTOR_ID: (doctorId) => `/doctor-info/by-doctor/${encodeURIComponent(String(doctorId))}`,
    GET_BY_ID: (id) => `/doctor-info/${encodeURIComponent(String(id))}`,
    UPDATE_BY_ID: (id) => `/doctor-info/${encodeURIComponent(String(id))}`,
  },
};

/* ===== AI ASSISTANT (chatbot médecin) ===== */
export const AI_ASSISTANT_PATHS = {
  HEALTH: "/assistant/health",
  CHAT: "/assistant/chat",
  VOICE_CHAT: "/assistant/voice/chat",
  DOCTOR_HEALTH: "/assistant/health",
  DOCTOR_CHAT: "/assistant/chat",
};

/* ===== ASSISTANTS (secrétaires/assistants humains du médecin) ===== */
export const ASSISTANTS_PATHS = {
    HEALTH: "/assistant/health",
  CHAT: "/assistant/chat",
  VOICE_CHAT: "/assistant/voice/chat",
  DOCTOR_HEALTH: "/assistant/health",
  DOCTOR_CHAT: "/assistant/chat",
  // POST /assistants  (headers: x-doctor-id, x-internal-secret)
  CREATE: "/assistants",

  // GET /assistants  (headers requis + query AssistantQueryDto)
  LIST: "/assistants",

  // GET /assistants?status=...&search=...
  FILTER_AND_SEARCH: ({ status, search } = {}) => {
    const params = new URLSearchParams();
    if (status) params.set("status", String(status));
    if (search) params.set("search", String(search));
    const query = params.toString();
    return query ? `/assistants?${query}` : "/assistants";
  },

  // GET /assistants/:assistantId  (ParseUUIDPipe -> id doit être un UUID valide)
  BY_ID: (assistantId) => `/assistants/${encodeURIComponent(String(assistantId))}`,

  // PATCH /assistants/:assistantId
  UPDATE: (assistantId) => `/assistants/${encodeURIComponent(String(assistantId))}`,

  // DELETE /assistants/:assistantId
  DELETE: (assistantId) => `/assistants/${encodeURIComponent(String(assistantId))}`,

  // POST /assistants/:assistantId/resend-invitation
  RESEND_INVITATION: (assistantId) =>
    `/assistants/${encodeURIComponent(String(assistantId))}/resend-invitation`,

  // GET /assistants/activation/verify?token=...  (route statique déclarée AVANT :assistantId, donc pas de collision)
  ACTIVATION_VERIFY: "/assistants/activation/verify",

  // POST /assistants/activation/set-password
  ACTIVATION_SET_PASSWORD: "/assistants/activation/set-password",

  // POST /assistants/auth/login
  AUTH_LOGIN: "/assistants/auth/login",

  // Appartient au microservice rendezvous, pas au controller assistants — gardé ici par commodité
  DOCTOR_RDV_AS_ASSISTANT: (doctorId) =>
    `/rendezvous/users/${encodeURIComponent(String(doctorId))}/medecin`,
};


/* ===== CONSULTATIONS ===== */
export const CONSULTATION_PATHS = {
  BASE: "",
  BY_ID: (id) => `/${encodeURIComponent(id)}`,
  BY_PATIENT: (userId, doctorId) =>
    doctorId
      ? `/patient/${encodeURIComponent(String(userId))}/doctor/${encodeURIComponent(String(doctorId))}`
      : `/patient/${encodeURIComponent(String(userId))}`,
  BY_DOCTOR: (doctorId) => `/doctor/${encodeURIComponent(doctorId)}`,
};

/* ===== SPECIALITY / NEPHROLOGY ===== */
export const SPECIALITY_PATHS = {
  NEPHROLOGY: {
    HISTORY_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/history`,
    HISTORY_BY_ID: (historyId) =>
      `/history/${encodeURIComponent(String(historyId))}`,
    EXAM_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/exam`,
    EXAM_BY_ID: (examId) =>
      `/exam/${encodeURIComponent(String(examId))}`,
    URINARY_UREMIA_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/urinary-uremia`,
    BIOLOGICAL_FOLLOW_UP_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/biological-follow-up`,
    NEPHROPROTECTION_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/nephroprotection`,
    SUMMARY_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/summary`,
    IMAGING_BY_PATIENT: (patientId) =>
      `/patients/${encodeURIComponent(String(patientId))}/imaging`,
  },
};

/* ===== PULMONOLOGY ===== */
export const PULMONOLOGY_PATHS = {
  BY_PATIENT: (patientId, resource) =>
    `/patients/${encodeURIComponent(String(patientId))}/${resource}`,
  BY_ID: (resource, id) =>
    `/${resource}/${encodeURIComponent(String(id))}`,
};

/* ===== LIKES ===== */
export const LIKES_PATHS = {
  BASE: "/",
  BY_POST: (postId) => `/?postId=${encodeURIComponent(postId)}`,
  STATUS: (postId, userId) =>
    `/status?postId=${encodeURIComponent(postId)}&userId=${encodeURIComponent(userId)}`,
};

/* ===== COMMENTS ===== */
export const COMMENTS_PATHS = {
  BASE: "/",
  BY_POST: (postId) => `/?postId=${encodeURIComponent(postId)}`,
  UPDATE: (id) => `/${encodeURIComponent(id)}`,
  DELETE: (id) => `/${encodeURIComponent(id)}`,
};

/* ===== CONNECTIONS ===== */
export const CONNECTIONS_PATHS = {
  BASE: "/connections",
  CREATE: "/connections",
  GET_ALL: "/connections",
  BY_USER: (userId) => `/connections/${encodeURIComponent(String(userId))}`,
  UPDATE: (id) => `/connections/${encodeURIComponent(String(id))}`,
  DELETE: (id) => `/connections/${encodeURIComponent(String(id))}`,
};

/* ===== FOLLOWS ===== */
export const FOLLOWS_PATHS = {
  FOLLOW: "/",

  GET_FOLLOWING: (followerId, followeeType = "doctor") => {
    if (!followerId) throw new Error("GET_FOLLOWING: followerId is required");
    const id = encodeURIComponent(String(followerId));
    const query = followeeType
      ? `?followeeType=${encodeURIComponent(String(followeeType))}`
      : "";
    return `/following/${id}${query}`;
  },

  GET_FOLLOWERS: (followeeId, followeeType = "doctor") => {
    if (!followeeId) throw new Error("GET_FOLLOWERS: followeeId is required");
    const id = encodeURIComponent(String(followeeId));
    const t = encodeURIComponent(String(followeeType || "doctor"));
    return `/followers/${id}/${t}`;
  },

  UNFOLLOW: (followerId, followeeId, followeeType = "doctor") => {
    if (!followerId || !followeeId) {
      throw new Error("UNFOLLOW: followerId and followeeId are required");
    }
    const a = encodeURIComponent(String(followerId));
    const b = encodeURIComponent(String(followeeId));
    const t = encodeURIComponent(String(followeeType || "doctor"));
    return `/${a}/${b}/${t}`;
  },
};

/* ===== AZURE HEALTH BOT ===== */
export const HEALTH_BOT_PATHS = Object.freeze({
  TOKEN: "/medical-assistant/token",
  REFRESH: "/medical-assistant/refresh",
});

/* ===== NOTIFICATIONS ===== */
export const NOTIFICATIONS_PATHS = {
  LIST: (userId, skip = 0, limit = 20, unreadOnly, cursor, since) => {
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
    });
    if (unreadOnly) {
      params.append('unreadOnly', 'true');
    }
    if (cursor) {
      params.append('cursor', String(cursor));
    }
    if (since) {
      params.append('since', String(since));
    }
    return `/${userId}?${params.toString()}`;
  },
  READ_ONE: (userId, id) => `/${userId}/read/${id}`,
  READ_ALL: (userId) => `/${userId}/read-all`,
  PUSH_SUBSCRIPTIONS: (userId) => `/${encodeURIComponent(String(userId))}/push-subscriptions`,
};

/* ===== CYCLEBOT ===== */
export const CYCLEBOT_PATHS = {
  HEALTH: "/cycle-bot/health",

  CHAT_START:   "/cycle-bot/chat/start",
  CHAT_MESSAGE: "/cycle-bot/chat/message",

  JOURNAL_LOG:      "/cycle-bot/journal/log",
  JOURNAL_SUMMARY:  "/cycle-bot/journal/summary",
  JOURNAL_ANALYSIS: "/cycle-bot/journal/analysis",
  JOURNAL_ENTRIES:  "/cycle-bot/journal/entries",

  FLOWS_LIST:   "/cycle-bot/flows",
  FLOWS_BY_ID:  (flowId)    => `/cycle-bot/flows/${flowId}`,
  FLOWS_START:  "/cycle-bot/flows/start",
  FLOWS_ANSWER: "/cycle-bot/flows/answer",
  FLOWS_REPORT: (sessionId) => `/cycle-bot/flows/report/${sessionId}`,
  FLOWS_DELETE: (sessionId) => `/cycle-bot/flows/${sessionId}`,
};

/* ===== SOUS PROFILES ===== */
export const SOUS_PROFILE_PATHS = {
  CREATE: "/",
  GET_ALL: "/",
  GET_BY_ID: (id) => `/${id}`,
  UPDATE: (id) => `/${id}`,
  SWITCH_PROFILE: "/session/switch-profile",
  RESTORE: "/session/restore",
  ADD_EMAIL: (id) => `/${id}/email`,
  DELETE: (id) => `/${id}`,
  REQUEST_SEPARATION: (id) => `/${id}/request-separation`,
  GRANT_ACCESS: (id) => `/${id}/grant-access`,
  REVOKE_ACCESS: (id) => `/${id}/revoke-access`,
  VERIFY_INVITATION_TOKEN: "/invitations/verify-token",
  ACCEPT_INVITATION: "/invitations/accept",
  VERIFY_TOKEN: "/emancipation/verify-token",
  SET_PASSWORD: "/emancipation/set-password",
  MANUAL_CHECK_BIRTHDAYS: "/check-birthdays-now/manual",
};

/* ===== ADMIN AUTH ===== */
export const ADMIN_AUTH_PATHS = {
  LOGIN:  '/admin/auth/login',
  ME:     '/admin/auth/me',
  LOGOUT: '/admin/auth/logout',
};

/* ===== ADMIN DOCTORS ===== */
export const ADMIN_DOCTORS_PATHS = {
  BASE:           '/admin/doctors',
  LIST:           '/admin/doctors',
  LIST_BY_STATUS: (status) => `/admin/doctors?status=${encodeURIComponent(status)}`,
  BY_ID:          (id) => `/admin/doctors/${id}`,
  VALIDATE:       (id) => `/admin/doctors/${id}/validate`,
  PENDING:        '/admin/doctors/pending',
  STATS:          '/admin/doctors/stats',
};

/* ===== ADMIN FEEDBACK ===== */
export const ADMIN_FEEDBACK_PATHS = {
  BASE: '/',
  LIST: '/',
  ME: '/me',
  AVERAGE: '/average',
  BY_ID: (id) => `/${id}`,
  BY_ROLE: (role) => `/role/${encodeURIComponent(role)}`,
  CREATE: '',
  UPDATE: (id) => `/${id}`,
  DELETE: (id) => `/${id}`,
};

/* ===== CNSS / BULLETIN DE SOINS ===== */
export const CNSS_PATHS = {
  ALL: "/",

  BY_ID: (cardId) =>
    `/${encodeURIComponent(String(cardId))}`,

  CREATE_BY_PATIENT: (userId) =>
    `/users/${encodeURIComponent(String(userId))}`,

  CREATE_BY_DOCTOR: (doctorId, patientId) =>
    `/doctors/${encodeURIComponent(String(doctorId))}/patients/${encodeURIComponent(String(patientId))}`,

  UPDATE: (cardId, verifierId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(verifierId))}`,

  DELETE: (cardId) =>
    `/${encodeURIComponent(String(cardId))}`,

  STATISTICS:        "/statistics",
  STATISTICS_GLOBAL: "/statistics/global",
  UNVERIFIED:        "/unverified",
  EXPIRING:          "/expiring",
  EXPIRED:           "/expired",

  BY_DOCTOR_PATIENT: (doctorId, patientId) =>
  `/doctors/${encodeURIComponent(String(doctorId))}/patients/${encodeURIComponent(String(patientId))}`,

  VERIFY: (identifiant) =>
    `/verify?identifiant=${encodeURIComponent(String(identifiant))}`,

  SEARCH_BY_NAME: (nomPrenom) =>
    `/search/name?nomPrenom=${encodeURIComponent(String(nomPrenom))}`,

  SEARCH_BY_CIN: (cin) =>
    `/search/cin?cin=${encodeURIComponent(String(cin))}`,

  BY_PATIENT: (patientId) =>
    `/patients/${encodeURIComponent(String(patientId))}`,

  DEPENDENTS_BY_PATIENT: (patientId) =>
    `/patients/${encodeURIComponent(String(patientId))}/dependents`,

  BY_CREATOR: (createdBy) =>
    `/creators/${encodeURIComponent(String(createdBy))}`,

  ALL_ENFANTS:           "/dependents/enfants",
  UNVERIFIED_DEPENDENTS: "/dependents/unverified",

  ADD_DEPENDENT: (cardId, userId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents`,

  DEPENDENTS_BY_CARD: (cardId, userId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents`,

  DEPENDENT_BY_ID: (cardId, userId, dependentId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents/${encodeURIComponent(String(dependentId))}`,

  UPDATE_DEPENDENT: (cardId, userId, dependentId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents/${encodeURIComponent(String(dependentId))}`,

  DELETE_DEPENDENT: (cardId, userId, dependentId) =>
    `/${encodeURIComponent(String(cardId))}/users/${encodeURIComponent(String(userId))}/dependents/${encodeURIComponent(String(dependentId))}`,

  CREATE_MEDICAL_ACT: (doctorId, patientId) =>
    `/doctors/${encodeURIComponent(String(doctorId))}/patients/${encodeURIComponent(String(patientId))}/acts`,

  ACTS_BY_PATIENT: (patientId, type) =>
    type
      ? `/patients/${encodeURIComponent(String(patientId))}/acts?type=${encodeURIComponent(String(type))}`
      : `/patients/${encodeURIComponent(String(patientId))}/acts`,

  DELETE_ACT: (actId) =>
    `/acts/${encodeURIComponent(String(actId))}`,
};

/* ===== PAYMENT (Flouci / ms-payment via gateway) ===== */
export const PAYMENT_PATHS = {
  INITIATE: "/payment/initiate",
  REFUND: (flouciPaymentId) =>
    `/payment/refund/${encodeURIComponent(String(flouciPaymentId))}`,
  CONFIRM: (flouciPaymentId) =>
    `/payment/confirm/${encodeURIComponent(String(flouciPaymentId))}`,
  HISTORY: "/payment/history",
  PLAN: (userId) => `/payment/plan/${encodeURIComponent(String(userId))}`,
  SUBSCRIPTION: (userId) => `/subscription/${encodeURIComponent(String(userId))}`,
  CANCEL_SUBSCRIPTION: "/subscription/cancel",
};

/* ===== GLOBAL EXPORT ===== */
const ENDPOINTS = {
  AUTH: AUTH_PATHS,
  OAUTH: OAUTH_URLS,
  USERS: USERS_PATHS,
  MEDICAL: MEDICAL_PATHS,
  CYCLES: CYCLES_PATHS,
  RELATIVES: RELATIVES_PATHS,
  POSTS: POSTS_PATHS,
  MEDICATION: MEDICATION_PATHS,
  MEDICATIONS_DATABASE: MEDICATIONS_DATABASE_PATHS,
  ANALYSE: ANALYSE_PATHS,
  VITALS: VITALS_PATHS,
  PRESCRIPTION: PRESCRIPTION_PATHS,
  EMERGENCY: EMERGENCY_PATHS,
  PLACES: PLACES_PATHS,
  MODERATION: MODERATION_PATHS,
  MESSAGES: MESSAGES_PATHS,
  REGISTER_DOCTOR: DOCTOR_REGISTER_PATHS,
  DOCTOR_AUTH: DOCTOR_AUTH_PATHS,
  EXTRACTION: EXTRACTION_PATHS,
  AVAILABILITY: AVAILABILITY_PATHS,
  RENDEZVOUS: RENDEZVOUS_PATHS,
  ROOM: ROOM_PATHS,
  LOCAL_PATIENT: LOCAL_PATIENT_PATHS,
  DOCTOR_PROFILE: DOCTOR_PROFILE_PATHS,
  AI_ASSISTANT: AI_ASSISTANT_PATHS,
    ASSISTANT: AI_ASSISTANT_PATHS,
  ASSISTANTS: ASSISTANTS_PATHS,
  CONSULTATION: CONSULTATION_PATHS,
  SPECIALITY: SPECIALITY_PATHS,
  PULMONOLOGY: PULMONOLOGY_PATHS,
  HEALTH_BOT: HEALTH_BOT_PATHS,
  LIKES: LIKES_PATHS,
  COMMENTS: COMMENTS_PATHS,
  EVENTS: EVENTS_PATHS,
  FOLLOWS: FOLLOWS_PATHS,
  CONNECTIONS: CONNECTIONS_PATHS,
  NOTIFICATIONS: NOTIFICATIONS_PATHS,
  MEDICAL_INTERRACTION: MEDICAL_INTERRACTION_PATHS,
  CNSS: CNSS_PATHS,
  ADMIN_AUTH: ADMIN_AUTH_PATHS,
  ADMIN_DOCTORS: ADMIN_DOCTORS_PATHS,
  ADMIN_FEEDBACK: ADMIN_FEEDBACK_PATHS,
  PAYMENT: PAYMENT_PATHS,
};

export default ENDPOINTS;
